'use client';

import { Button } from '@pickleballinc/ui/components/ui/button';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { ActionButtons, ResponsivePlayedMatchesList } from '@/components/molecules';
import { FinalTieBreakerScoreModal } from '@/components/molecules/tie-breaker/FinalTieBreakerScoreModal';
import { MatchTeamsScore } from '@/components/molecules/tie-breaker/MatchTeamsScore';
import { ResponsiveNextInPlayersList } from '@/components/molecules/tie-breaker/NextInPlayersList';
import { PlayingNowCard } from '@/components/molecules/tie-breaker/PlayingNowCard';
import { ModalWrapper } from '@/components/organisms';
import { Match, Matchup } from '@/entities';
import { TieBreakerFormatType, TieBreakerScoreFormat, TieBreakerSettings, TieBreakerType } from '@/entities/Matchup';
import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum, MatchupStatusApiMap, MatchupStatusEnum } from '@/entities/matchup/constants';
import { SequenceStatus, TieBreakerSequence } from '@/entities/rotations/GameSequence';
import { useLastSequenceInfoQuery } from '@/hooks/tie-breaker/useLastSequnceInfo';
import { useGameSequencesMutation } from '@/hooks/tie-breaker/useRotationsMutation';
import {
	buildSequencePayloadForSave,
	getLastActiveSequence,
	getNextInList,
	isValidScore,
	mapPlayedSequencesToMatches,
	PlayedMatch
} from '@/infrastructure/utils/tieBreakerHelpers';
import { useTiebreakerStore } from '@/store/tie-breaker-store';
import { DateUtil } from '@/utils/date';

interface TieBreakerRotationCardProps {
	gameSequences: TieBreakerSequence[];
	teamOneTitle: string;
	teamTwoTitle: string;
	winBy?: number;
	tieBreakerScoreFormat: TieBreakerScoreFormat | null;
	TieBreakerSettings: TieBreakerSettings | null;
	teamOneUuid: string;
	teamTwoUuid: string;
	tieBreakerMatchId: string;
	tieBreakerMatchStatus: number;
	matchupId: string;
	tieBreakerRotatePlayerAfter: number;
	tieBreakerType?: TieBreakerType;
	matchupData?: Matchup;
	isPublicMatchResultPage?: boolean;
	tieBreakerMatchCompletedType?: number;
	tieBreakerTitle: string;
	canUserEditScores: boolean;
	onCancelClick?: () => void;
	tieBreakerMatch?: Match;
}

export const TieBreakerRotationCard = ({
	gameSequences,
	teamOneTitle,
	teamTwoTitle,
	tieBreakerScoreFormat,
	TieBreakerSettings,
	tieBreakerMatchStatus,
	matchupId,
	tieBreakerRotatePlayerAfter,
	isPublicMatchResultPage = false,
	tieBreakerMatchCompletedType,
	tieBreakerTitle,
	canUserEditScores,
	onCancelClick,
	tieBreakerMatch,
	matchupData
}: TieBreakerRotationCardProps) => {
	const sequences = [...gameSequences].sort((a, b) => a.sequenceNumber - b.sequenceNumber);

	const localRef = useRef<HTMLDivElement>(null);
	const setTiebreakerRef = useTiebreakerStore((state) => state.setTiebreakerRef);

	const lastScoreSequence = getLastActiveSequence(sequences);

	const gameSequencesMatchId = sequences[0]?.matchUuid || '';

	const { data: lastSequence } = useLastSequenceInfoQuery(gameSequencesMatchId, sequences[0]?.gameNumber ?? 0);

	const [teamOneScore, setTeamOneScore] = useState<number | ''>('');
	const [teamTwoScore, setTeamTwoScore] = useState<number | ''>('');

	const [editScores, setEditScores] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);

	const sequenceData = lastSequence?.data?.sequence;

	const teamOneNextUp = lastSequence?.data?.teamOneNextUp ?? [];
	const teamTwoNextUp = lastSequence?.data?.teamTwoNextUp ?? [];

	const lastSequenceInProgress = lastSequence?.data.sequence.sequenceStatus === SequenceStatus.IN_PROGRESS;

	const nextToPlay = sequenceData ?? null;

	const firstSequenceUnplayed = sequences[0]?.teamOneScore === 0 && sequences[0]?.teamTwoScore === 0;

	const invalidInputs = !isValidScore(String(teamOneScore)) || !isValidScore(String(teamTwoScore));
	const isEmpty = (val: unknown) => val === null || val === undefined;

	useEffect(() => {
		if (localRef.current) {
			setTiebreakerRef(localRef.current);
		}
	}, [setTiebreakerRef]);

	const { createSequence, isCreatingSequence, invalidateQueries } = useGameSequencesMutation(
		gameSequences[0]?.matchUuid || '',
		gameSequences[0]?.gameNumber.toString() || '',
		matchupId
	);

	const isSequenceInProgress = isCreatingSequence;

	const isMatchOverAndCompleted =
		tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] &&
		tieBreakerMatchCompletedType !== MatchCompletedEnum.WONT_BE_PLAYED_MATCH_COMPLETED_TYPE;

	const isMatchupCompleted = matchupData?.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;
	const canEditCompletedSequences = isMatchOverAndCompleted && !isMatchupCompleted;

	useEffect(() => {
		if (lastScoreSequence && typeof lastScoreSequence.teamOneScore === 'number' && typeof lastScoreSequence.teamTwoScore === 'number') {
			setTeamOneScore(lastScoreSequence.teamOneScore);

			setTeamTwoScore(lastScoreSequence.teamTwoScore);
		}
	}, [lastScoreSequence]);

	const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: number | '') => void, _tieBreakerRotatePlayerAfter?: number) => {
		const val = e.target.value;

		if (!isValidScore(val)) return;

		const num = val === '' ? '' : Number(val);

		setter(num);
	};

	const handleReEnter = () => {
		setTeamOneScore(lastScoreSequence?.teamOneScore || 0);
		setTeamTwoScore(lastScoreSequence?.teamTwoScore || 0);
		setShowConfirmModal(false);
	};

	const saveSequence = async () => {
		if (!sequenceData) return;

		const payload = buildSequencePayloadForSave({
			teamOneScore: Number(teamOneScore),
			teamTwoScore: Number(teamTwoScore),
			gameSequences,
			lastSequence: sequenceData,
			sequences: sequences,
			teamOneNextUp,
			teamTwoNextUp,
			isDoubles: TieBreakerSettings?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES
		});

		createSequence.mutate(payload, {
			onSuccess: async () => {
				await invalidateQueries();
			}
		});
	};

	const handleConfirmSaveAnyway = () => {
		setShowConfirmModal(false);
		saveSequence();
	};

	const handleSave = () => {
		const maxScore = tieBreakerScoreFormat?.game1Max;
		const scoreExceeded = maxScore !== undefined && (Number(teamOneScore) > maxScore || Number(teamTwoScore) > maxScore);

		if (scoreExceeded) {
			setShowConfirmModal(true);
			return;
		}

		saveSequence();
	};

	const updatePlayedTieBreakerSequences = (updated: PlayedMatch[]) => {
		if (!sequenceData) {
			return;
		}

		const editedScores = updated.map((match) => ({
			uuid: match.uuid,
			teamOneScore: Number(match.rightScore),
			teamTwoScore: Number(match.leftScore)
		}));

		const payload = buildSequencePayloadForSave({
			teamOneScore: Number(teamOneScore),
			teamTwoScore: Number(teamTwoScore),
			gameSequences,
			lastSequence: sequenceData,
			sequences: sequences,
			teamOneNextUp,
			teamTwoNextUp,
			isDoubles: TieBreakerSettings?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES,
			editedScores
		});

		createSequence.mutate(payload, {
			onSuccess: async () => {
				await invalidateQueries();
			}
		});
		setEditScores(false);
	};

	const createScoreHandleChange =
		(setter: (v: number | '') => void, tieBreakerRotatePlayerAfter: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
			handleScoreChange(e, setter, tieBreakerRotatePlayerAfter);
		};

	const renderSaveButton = () => {
		return (
			tieBreakerMatchStatus !== MatchupStatusApiMap[MatchupStatusEnum.COMPLETED_MATCHUP_STATUS] && (
				<ActionButtons
					className="w-full justify-center"
					submitButtonClassName={clsx(
						'w-fit !h-8 disabled:cursor-not-allowed !gap-3 !mt-2 !py-1 !px-2 !text-xs',
						isMatchOverAndCompleted && '!p-0'
					)}
					closeButtonClassName="!w-fit !text-darkorange-600 w-full hidden"
					onClose={() => {}}
					onSubmit={handleSave}
					submitButtonText={!isMatchOverAndCompleted ? 'Save' : 'Save Completed DreamBreaker'}
					closeButtonText="Cancel"
					iconClassName="size-4"
					submitButtonDisabled={
						!isMatchOverAndCompleted && (isSequenceInProgress || invalidInputs || isEmpty(teamOneScore) || isEmpty(teamTwoScore))
					}
					isLoading={isSequenceInProgress}
				/>
			)
		);
	};

	return (
		<div ref={localRef} className="pt-8">
			<div className="rounded-3xl border border-gray-400 py-6 px-0 sm:px-4 md:px-9 flex flex-col bg-white">
				<div className="px-4 lg:px-10 xl:px-48 gap-6">
					<div className="flex flex-col md:flex-row w-full justify-between md:items-center">
						<div className="flex flex-col gap-0.5">
							<p className="text-sm font-semibold">{tieBreakerTitle}</p>
							{tieBreakerMatch?.matchPlannedStart ? (
								<p className="text-sm text-gray-600">
									{DateUtil.formatUTCInTimezone(
										tieBreakerMatch.matchPlannedStart,
										'MMM D YYYY',
										tieBreakerMatch.timezone?.ianaTzIdentifier
									)}
									{' | '}
									{DateUtil.formatUTCInTimezone(
										tieBreakerMatch.matchPlannedStart,
										'h:mm A',
										tieBreakerMatch.timezone?.ianaTzIdentifier
									)}
									{tieBreakerMatch.timezone?.abbreviation ? ` ${tieBreakerMatch.timezone.abbreviation}` : ''}
									{tieBreakerMatch.courtTitle ? ` | Court: ${tieBreakerMatch.courtTitle}` : ''}
									{tieBreakerScoreFormat?.title ? ` | ${tieBreakerScoreFormat.title}` : ''}
								</p>
							) : null}
						</div>
						{!isMatchOverAndCompleted ? (
							<div className="flex flex-col items-end gap-2">
								{canUserEditScores ? (
									<Button size="sm" className="text-xs font-semibold" onClick={() => setShowFinalScoreModal(true)}>
										Enter final score
									</Button>
								) : null}
								{onCancelClick ? (
									<Button variant="outline" onClick={onCancelClick}>
										Cancel {tieBreakerTitle.toLowerCase()} match
									</Button>
								) : null}
							</div>
						) : null}
						{canEditCompletedSequences && canUserEditScores && tieBreakerMatch?.tieBreakerIsDirectFinalScore ? (
							<Button type="button" onClick={() => setShowFinalScoreModal(true)} size="sm">
								Edit
							</Button>
						) : null}
					</div>

					{isMatchOverAndCompleted ? <p className="text-lg py-4 font-semibold text-darkorange-400">{tieBreakerTitle} completed!</p> : null}

					<MatchTeamsScore
						teamOneTitle={teamOneTitle}
						teamTwoTitle={teamTwoTitle}
						teamOneTotal={lastScoreSequence?.teamOneScore || 0}
						teamTwoTotal={lastScoreSequence?.teamTwoScore || 0}
						isMatchOverAndCompleted={isMatchOverAndCompleted}
						tieBreakerIsDirectFinalScore={Boolean(tieBreakerMatch?.tieBreakerIsDirectFinalScore)}
					/>
					{isMatchOverAndCompleted && !isPublicMatchResultPage ? (
						<div className="flex justify-center pt-4">{renderSaveButton()}</div>
					) : null}

					<PlayingNowCard
						nextToPlay={
							nextToPlay
								? {
										teamOnePlayerOneName:
											// last sequence in progress (never satisfied it seems); firstSequenceUnplayed satisfied only before first sequence is played
											lastSequenceInProgress || firstSequenceUnplayed
												? (sequences[sequences.length - 1]?.teamOnePlayerOneName ?? '')
												: (teamOneNextUp[0]?.playerOneName ?? ''),
										teamOnePlayerTwoName:
											lastSequenceInProgress || firstSequenceUnplayed
												? (sequences[sequences.length - 1]?.teamOnePlayerTwoName ?? '')
												: (teamOneNextUp[0]?.playerTwoName ?? undefined),
										teamTwoPlayerOneName:
											lastSequenceInProgress || firstSequenceUnplayed
												? (sequences[sequences.length - 1]?.teamTwoPlayerOneName ?? '')
												: (teamTwoNextUp[0]?.playerOneName ?? ''),
										teamTwoPlayerTwoName:
											lastSequenceInProgress || firstSequenceUnplayed
												? (sequences[sequences.length - 1]?.teamTwoPlayerTwoName ?? '')
												: (teamTwoNextUp[0]?.playerTwoName ?? undefined)
									}
								: null
						}
						teamOneScore={teamOneScore}
						teamTwoScore={teamTwoScore}
						setTeamOneScore={setTeamOneScore}
						setTeamTwoScore={setTeamTwoScore}
						createScoreHandleChange={createScoreHandleChange}
						tieBreakerRotatePlayerAfter={tieBreakerRotatePlayerAfter}
						isMatchOver={isMatchOverAndCompleted}
						isMatchOverAndCompleted={isMatchOverAndCompleted}
						currentSequenceNumber={
							sequences[0]?.teamOneScore === 0 && sequences[0]?.teamTwoScore === 0
								? 1
								: lastSequenceInProgress
									? sequences.length
									: sequences.length + 1
						}
						renderSaveButton={renderSaveButton}
						isPublicMatchResultPage={isPublicMatchResultPage}
						canUserEditScores={canUserEditScores}
					/>
					{!tieBreakerMatch?.tieBreakerIsDirectFinalScore && (
						<ResponsivePlayedMatchesList
							playedMatches={mapPlayedSequencesToMatches(sequences) as any}
							isMatchOverAndCompleted={isMatchOverAndCompleted}
							editScores={editScores}
							setEditScores={setEditScores}
							updatePlayedTieBreakerSequences={updatePlayedTieBreakerSequences}
							isEditing={editScores}
							isUpdatingSequence={isCreatingSequence}
							isPublicMatchResultPage={isPublicMatchResultPage}
							canUserEditScores={canUserEditScores}
							canEditCompletedSequences={canEditCompletedSequences}
						/>
					)}

					{!isMatchOverAndCompleted && mapPlayedSequencesToMatches(gameSequences) && (
						<div className="w-full py-2 border-b border-b-darkorange-400" />
					)}

					{!isMatchOverAndCompleted && (
						<ResponsiveNextInPlayersList
							nextInTeamOne={getNextInList(teamOneNextUp, lastSequenceInProgress, firstSequenceUnplayed)}
							nextInTeamTwo={getNextInList(teamTwoNextUp, lastSequenceInProgress, firstSequenceUnplayed)}
							isMatchOverAndCompleted={isMatchOverAndCompleted}
							teamOneTitle={teamOneTitle}
							teamTwoTitle={teamTwoTitle}
						/>
					)}
				</div>
			</div>

			<FinalTieBreakerScoreModal
				isOpen={showFinalScoreModal}
				onClose={() => setShowFinalScoreModal(false)}
				gameSequences={gameSequences}
				matchupId={matchupId}
				awayTeamTitle={teamTwoTitle}
				homeTeamTitle={teamOneTitle}
				tieBreakerScoreFormat={tieBreakerScoreFormat}
				awayTeamScore={lastScoreSequence?.teamTwoScore.toString() ?? undefined}
				homeTeamScore={lastScoreSequence?.teamOneScore.toString() ?? undefined}
			/>

			{showConfirmModal ? (
				<ModalWrapper title={'Invalid score'} onClose={handleReEnter} className="max-w-[343px] gap-0 p-6 text-start">
					<p className="font-normal pt-4 pb-10 text-gray-500">
						One of the scores do not meet the score format for this game. If you have done so in error, please correct the score by
						clicking “Re-enter”. If the score is correct, click “Save anyway”
					</p>
					<div className="flex w-full justify-between gap-4">
						<button onClick={handleReEnter} className="px-4 py-2 text-sm border w-full border-gray-300 rounded-md hover:bg-gray-100">
							Re-enter
						</button>
						<button
							onClick={handleConfirmSaveAnyway}
							className="px-4 py-2 text-sm bg-darkorange-400 w-full text-white rounded-md hover:bg-darkorange-500"
						>
							Save anyway
						</button>
					</div>
				</ModalWrapper>
			) : null}
		</div>
	);
};
